import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import MediaUploader from "./MediaUploader";

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", text: "", media: null });

  const load = useCallback(() => { api.get("/announcements").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ title: "", text: "", media: null }); setShow(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, text: a.text || "", media: a.media || null }); setShow(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      if (editing) await api.put(`/admin/announcements/${editing.id}`, form);
      else await api.post("/admin/announcements", form);
      toast.success("Saved"); setShow(false); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete announcement?")) return; await api.delete(`/admin/announcements/${id}`); load(); };

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading font-black uppercase text-xl text-[#F0EDEA]">Announcements</h1>
        <button onClick={openNew} className="btn-skew btn-primary text-xs" data-testid="add-announcement-btn"><span><Plus size={16} /> Add</span></button>
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="bg-[#1C1C1C] border border-[#333] rounded-xl p-3 flex gap-3" data-testid={`admin-announcement-${a.id}`}>
            {a.media && (a.media.kind === "video" ? <video src={mediaUrl(a.media)} className="w-16 h-16 object-cover rounded-lg" muted /> : <img src={mediaUrl(a.media)} className="w-16 h-16 object-cover rounded-lg" alt="" />)}
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold uppercase text-[#F0EDEA] text-sm">{a.title}</h3>
              <p className="text-[#A8A2A0] text-xs line-clamp-2">{a.text}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => openEdit(a)} className="text-[#A8A2A0] hover:text-[#E60000]"><Pencil size={16} /></button>
              <button onClick={() => remove(a.id)} className="text-[#A8A2A0] hover:text-[#E60000]"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-[#A8A2A0] text-center py-8">No announcements yet.</div>}
      </div>

      {show && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-end sm:items-center justify-center" data-testid="announcement-form">
          <div className="bg-[#111] border border-[#333] w-full sm:max-w-lg sm:rounded-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#111] border-b border-[#333] px-5 h-14 flex items-center justify-between">
              <h2 className="font-heading font-bold uppercase text-[#F0EDEA]">{editing ? "Edit" : "New"} Announcement</h2>
              <button onClick={() => setShow(false)} className="text-[#A8A2A0]"><X size={24} /></button>
            </div>
            <div className="p-5 space-y-3">
              <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[#141414] border border-[#333] rounded-lg h-11 px-3 text-[#F0EDEA] focus:border-[#E60000] outline-none" data-testid="announcement-title" />
              <textarea placeholder="Text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full bg-[#141414] border border-[#333] rounded-lg h-28 p-3 text-[#F0EDEA] focus:border-[#E60000] outline-none" data-testid="announcement-text" />
              <MediaUploader value={form.media ? [form.media] : []} onChange={(m) => setForm({ ...form, media: m[m.length - 1] || null })} />
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShow(false)} className="btn-skew btn-secondary flex-1 text-sm"><span>Cancel</span></button>
                <button onClick={save} className="btn-skew btn-primary flex-1 text-sm" data-testid="save-announcement"><span>Save</span></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
